import type { Vec2 } from './vec';
import { add, sub, scale, dot, cross, dist } from './vec';

const eps = 1e-9;

/**
 * Compute the intersection of two infinite lines.
 *
 * Both lines are defined by two points: p1, p2 define the first line;
 * q1, q2 define the second line.
 *
 * Returns the intersection point if the lines meet, or null if they are
 * parallel or collinear (collinear lines do not have a unique intersection).
 */
export function lineLineIntersect(p1: Vec2, p2: Vec2, q1: Vec2, q2: Vec2): Vec2 | null {
  // Direction vectors
  const d1 = sub(p2, p1); // p1 + t * d1
  const d2 = sub(q2, q1); // q1 + s * d2

  // Cross product determines if lines are parallel
  const denom = cross(d1, d2);
  if (Math.abs(denom) < eps) {
    return null; // Parallel or collinear
  }

  // Solve: p1 + t * d1 = q1 + s * d2
  // Rearrange: t * d1 - s * d2 = q1 - p1
  // Using cross product to eliminate variables:
  // t = cross(q1 - p1, d2) / cross(d1, d2)
  const q1_minus_p1 = sub(q1, p1);
  const t = cross(q1_minus_p1, d2) / denom;

  // Compute intersection point
  return add(p1, scale(d1, t));
}

/**
 * Compute intersection(s) of a circle and a line.
 *
 * The circle is centered at O with radius r.
 * The line is defined by two points p1 and p2.
 *
 * Returns an array of 0, 1, or 2 intersection points, ordered by (x, y) ascending.
 * A tangent line results in exactly one point (the discriminant equals 0 within eps = 1e-9).
 * A line that misses returns an empty array.
 *
 * Ordering: when multiple points exist, they are sorted first by x, then by y.
 */
export function circleLineIntersect(O: Vec2, r: number, p1: Vec2, p2: Vec2): Vec2[] {
  const d = sub(p2, p1); // Direction vector along line
  const f = sub(p1, O); // Vector from circle center to line start point

  // Quadratic equation: ||p1 + t*d - O||^2 = r^2
  // ||f + t*d||^2 = r^2
  // (f + t*d) · (f + t*d) = r^2
  // f·f + 2t(f·d) + t^2(d·d) = r^2
  // (d·d)t^2 + 2(f·d)t + (f·f - r^2) = 0

  const a = dot(d, d);
  const b = 2 * dot(f, d);
  const c = dot(f, f) - r * r;

  const discriminant = b * b - 4 * a * c;

  if (discriminant < -eps) {
    return []; // No intersection
  }

  if (Math.abs(discriminant) < eps) {
    // Tangent: exactly one point
    const t = -b / (2 * a);
    const point = add(p1, scale(d, t));
    return [point];
  }

  // Two intersections
  const sqrtDisc = Math.sqrt(discriminant);
  const t1 = (-b - sqrtDisc) / (2 * a);
  const t2 = (-b + sqrtDisc) / (2 * a);

  const point1 = add(p1, scale(d, t1));
  const point2 = add(p1, scale(d, t2));

  // Sort by (x, y) ascending
  const points = [point1, point2];
  points.sort((a, b) => {
    if (Math.abs(a.x - b.x) > eps) {
      return a.x - b.x;
    }
    return a.y - b.y;
  });

  return points;
}

/**
 * Compute intersection(s) of two circles.
 *
 * Circle 1 is centered at O1 with radius r1.
 * Circle 2 is centered at O2 with radius r2.
 *
 * Returns an array of 0, 1, or 2 intersection points, ordered by (x, y) ascending.
 * External or internal tangency results in exactly one point.
 * Non-intersecting, one-inside-other (not touching), or concentric circles return empty array.
 *
 * Ordering: when multiple points exist, they are sorted first by x, then by y.
 */
export function circleCircleIntersect(O1: Vec2, r1: number, O2: Vec2, r2: number): Vec2[] {
  const d = dist(O1, O2); // Distance between centers

  // Check for non-intersection or tangency cases
  const sum_r = r1 + r2;
  const diff_r = Math.abs(r1 - r2);

  if (d > sum_r + eps) {
    return []; // Too far apart
  }
  if (d < diff_r - eps) {
    return []; // One inside the other, not touching
  }
  if (d < eps && Math.abs(r1 - r2) < eps) {
    return []; // Concentric and equal radius
  }

  // Check for external tangency
  if (Math.abs(d - sum_r) < eps) {
    // One tangent point on the line between centers
    const u = scale(sub(O2, O1), r1 / d);
    return [add(O1, u)];
  }

  // Check for internal tangency
  if (Math.abs(d - diff_r) < eps) {
    // One tangent point on the line between centers
    const u = scale(sub(O2, O1), r1 / d);
    return [add(O1, u)];
  }

  // Two intersection points: reduce to a line (radical line) and solve with circle-line
  // The radical line is perpendicular to O1-O2 and at distance:
  // a = (d^2 + r1^2 - r2^2) / (2*d)

  const d_sq = d * d;
  const a = (d_sq + r1 * r1 - r2 * r2) / (2 * d);

  // Point on the line O1-O2 at distance a from O1
  const u = scale(sub(O2, O1), a / d);
  const p = add(O1, u);

  // Distance from p to the intersection points (half-chord length)
  // h^2 = r1^2 - a^2
  const h_sq = r1 * r1 - a * a;

  if (h_sq < -eps) {
    return []; // Numerical error, should not happen
  }

  if (Math.abs(h_sq) < eps) {
    // Tangent case (already handled above, but for safety)
    return [p];
  }

  const h = Math.sqrt(h_sq);

  // Direction perpendicular to O1-O2
  const dir = sub(O2, O1);
  const perp_dir = { x: -dir.y, y: dir.x };
  const perp_unit = scale(perp_dir, 1 / d);

  const point1 = add(p, scale(perp_unit, h));
  const point2 = add(p, scale(perp_unit, -h));

  // Sort by (x, y) ascending
  const points = [point1, point2];
  points.sort((a, b) => {
    if (Math.abs(a.x - b.x) > eps) {
      return a.x - b.x;
    }
    return a.y - b.y;
  });

  return points;
}
